<template>
  <section :class="$style.container" data-luko-tracking="MediaCarrousel">
    <Wrapper :class="$style.inner">
      <Title
        :class="$style.title"
        :title-params="primary.title"
        is-extra-title
      />
      <RichText
        :class="$style.description"
        :text-params="primary.description"
      />
      <div :class="$style.content">
        <div
          v-observe-visibility="{
            callback: sectionOnScreen,
            intersection: {
              threshold: 1,
            },
          }"
          :class="$style.view"
        >
          <template v-for="(item, itemIndex) in items">
            <template v-if="item.video.url">
              <video
                v-show="isActive(itemIndex)"
                :key="'video-' + itemIndex"
                :ref="'video-' + itemIndex"
                :class="[$style.mediaContainer]"
                muted="muted"
              >
                <source :src="item.video.url" type="video/mp4" />
              </video>
            </template>
            <template v-if="item.image.url">
              <PImage
                v-show="isActive(itemIndex)"
                :key="'image-' + itemIndex"
                :ref="'image-' + itemIndex"
                :image-object="item.image"
                :class="[$style.mediaContainer]"
              />
            </template>
          </template>
        </div>

        <div :class="$style.desc">
          <div
            v-for="(item, itemIndex) in items"
            :key="itemIndex"
            :class="[$style.item, { [$style.itemActive]: isActive(itemIndex) }]"
            @click="setActiveIndex(itemIndex)"
            @mouseenter="onMouseEnter(itemIndex)"
            @mouseleave="onMouseLeave(itemIndex)"
          >
            <div :class="$style.iconContainer">
              <RadialProgressBar
                :class="$style.circleProgress"
                :completed-steps="getProgress(itemIndex)"
                :animate-speed="getProgressSpeed(itemIndex)"
                :diameter="72"
                :total-steps="100"
                inner-stroke-color="transparent"
                :stroke-width="4"
              >
                <div
                  :class="[
                    $style.itemIcon,
                    { [$style.itemActive]: isActive(itemIndex) },
                    {
                      [$style.hide]: itemIndex >= mediaItems.length,
                    },
                  ]"
                >
                  {{ itemIndex + 1 }}
                </div>
              </RadialProgressBar>
            </div>
            <div :class="$style.infos">
              <RichText :text-params="item.description" />
            </div>
          </div>
          <PLink
            :link-object="primary.cta_link"
            is-highlight
            color="bluko"
            :class="$style.cta"
          >
            {{ primary.cta_text }}
          </PLink>
        </div>
      </div>
    </Wrapper>
  </section>
</template>

<script>
import RadialProgressBar from 'vue-radial-progress/src/RadialProgressBar.vue'

import Title from '~/components/Title'
import RichText from '~/components/RichText'
import PLink from '~/components/PLink'

export default {
  name: 'MediaCarrousel',

  components: {
    Title,
    RichText,
    PLink,
    RadialProgressBar,
  },

  props: {
    primary: {
      type: Object,
      default: () => ({}),
    },
    items: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      hasPlayedOnce: false,
      activeIndex: 0,
      prog: {},
      timeoutFct: null,
      activeVideoProgression: 0,
      timerFct: null,
    }
  },

  computed: {
    activeVideo() {
      const video = this.$refs['video-' + this.activeIndex]
      return video
        ? video[0]
        : {
            duration: 0,
            play() {
              return true
            },
            pause() {
              return true
            },
          }
    },

    progressions: {
      get() {
        return this.prog
      },
      set(newValue) {
        this.prog = newValue
      },
    },

    itemDuration() {
      return Math.round(this.activeVideo.duration + 4)
    },

    mediaItems() {
      return this.items.filter((item) => item.video.url || item.image.url)
    },
  },

  methods: {
    onMouseEnter(index) {
      if (this.isActive(index)) {
        this.activeVideo.pause()
        if (this.timerFct) clearInterval(this.timerFct)
      }
    },

    onMouseLeave(index) {
      if (this.isActive(index)) {
        this.activeVideo.play()
        this.startTimer()
      }
    },

    toNextVideo() {
      this.setActiveIndex((this.activeIndex + 1) % this.mediaItems.length)
    },

    sectionOnScreen(onScreen) {
      if (onScreen && !this.hasPlayedOnce) {
        this.setActiveIndex(0)
        this.hasPlayedOnce = true
      }
    },

    startTimer() {
      if (this.timerFct) clearInterval(this.timerFct)

      this.timerFct = setInterval(() => {
        this.activeVideoProgression += 0.2

        if (this.activeVideoProgression > this.itemDuration) {
          this.activeVideoProgression = 0
          clearInterval(this.timerFct)
          this.toNextVideo()
        }

        this.onVideoProgress()
      }, 200)
    },

    onVideoProgress() {
      const dataProg = { ...this.prog }
      dataProg[this.activeIndex] =
        (this.activeVideoProgression * 100) / this.itemDuration
      this.progressions = dataProg
    },

    resetVideoTimers() {
      this.activeVideo.currentTime = 0
      this.activeVideoProgression = 0
      this.onVideoProgress()
    },

    setActiveIndex(index) {
      if (index >= this.mediaItems.length) {
        index = this.mediaItems.length - 1
      }

      this.activeVideo.pause()
      this.resetVideoTimers()
      this.activeIndex = index
      this.startTimer()
      this.activeVideo.play()
    },

    isActive(index) {
      if (
        this.activeIndex >= this.mediaItems.length - 1 &&
        index > this.activeIndex
      )
        return true

      return index === this.activeIndex
    },

    getProgress(index) {
      if (!this.isActive(index)) return 0
      return this.progressions[index]
    },

    getProgressSpeed(index) {
      if (!this.isActive(index)) return 1000
      return 200
    },
  },
}
</script>

<style lang="scss" module>
.container {
  margin: 80px 0;
}

.inner {
  padding: 20px 24px;
}

.content {
  display: flex;
  gap: 40px;
  align-items: center;
  justify-content: center;
  margin-top: 60px;

  @include below(small) {
    flex-direction: column;
    gap: 0;
  }
}

.title {
  max-width: 640px;
  margin: 0 auto;

  text-align: center;
}
.description {
  @include typo-body;
  max-width: 840px;
  margin: 0 auto;

  color: $gray-700;
  text-align: center;
}

.view {
  display: block;
  max-width: 473px;

  transform: translateX(-20px);
}

.mediaContainer {
  width: 50vw;
  max-width: 474px;
}

.item {
  display: flex;
  align-items: center;
  max-width: 473px;

  color: $gray-400;
  text-align: left;

  cursor: pointer;

  transition-duration: 0.3s;
  transition-property: color, background-colors;

  &:not(:first-child) {
    margin-top: 20px;
  }
  @include below(small) {
    &:not(:first-child) {
      margin-top: 20px;
    }
  }
}

.iconContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 24px;
  padding: 12px;
  border-radius: 48px;
}

.circleProgress circle:last-child {
  stroke: $gray-400 !important;
}

.itemActive .circleProgress circle:last-child {
  stroke: $bluko-500 !important;
}

.itemIcon {
  @include typo-title;
  width: 48px;
  height: 48px;
  border-radius: 50%;

  line-height: 48px;
  text-align: center;

  background-color: $gray-50;
  opacity: 0.5;

  transition-duration: 0.3s;
  transition-property: opacity, background-color;

  &.itemActive {
    color: $bluko-500;

    background-color: $bluko-50;
    opacity: 1;
  }
}

.itemActive {
  color: $gray-1000;
}

.cta {
  margin-top: 56px;
}

.hide {
  opacity: 0 !important;
}
</style>
